import { supabase } from '@/integrations/supabase/client';

export interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateProfileData {
  display_name?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export class UserProfileService {
  /**
   * Get the current user's profile
   */
  static async getUserProfile(): Promise<UserProfile | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile, error } = await (supabase as any)
        .from('watt_users')
        .select('id, email, display_name, created_at, updated_at')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      return profile;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  /**
   * Update the user's profile information
   */
  static async updateProfile(data: UpdateProfileData): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await (supabase as any)
        .from('watt_users')
        .update({
          display_name: data.display_name,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating profile:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      return false;
    }
  }

  /**
   * Change the user's password
   */
  static async changePassword(data: ChangePasswordData): Promise<{ success: boolean; error?: string }> {
    try {
      // First verify the current password by attempting to sign in
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser?.email) {
        return { success: false, error: 'No authenticated user found' };
      }

      // Verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: currentUser.email,
        password: data.currentPassword
      });

      if (signInError) {
        return { success: false, error: 'Current password is incorrect' };
      }

      // Update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.newPassword
      });

      if (updateError) {
        return { success: false, error: updateError.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error changing password:', error);
      return { success: false, error: 'An unexpected error occurred' };
    }
  }
} 